import { useFormik } from 'formik';
import { useCallback, useMemo } from 'react';
import * as yup from 'yup';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useAgeGate } from '../contexts/AgeGateContext';

interface DateOfBirthFormValues {
    month: string;
    day: string;
    year: string;
}

interface UseDateOfBirthFormReturn {
    formik: ReturnType<typeof useFormik<DateOfBirthFormValues>>;
    monthOptions: { label: string; value: string }[];
    dayOptions: { label: string; value: string }[];
    handleFieldChange: (fieldName: string, value: string) => void;
    isAgeValid: boolean | null;
    isConfirmingAge: boolean;
}

export const useDateOfBirthForm = (): UseDateOfBirthFormReturn => {
    const { t } = useTranslation();
    const { isAgeValid, setIsAgeValid, isConfirmingAge, setDateOfBirth } = useAgeGate();

    const generateMonthOptions = (): { label: string; value: string }[] => {
        const monthNames = [
            t('epic.esport.gated.dob.month.january'),
            t('epic.esport.gated.dob.month.february'),
            t('epic.esport.gated.dob.month.march'),
            t('epic.esport.gated.dob.month.april'),
            t('epic.esport.gated.dob.month.may'),
            t('epic.esport.gated.dob.month.june'),
            t('epic.esport.gated.dob.month.july'),
            t('epic.esport.gated.dob.month.august'),
            t('epic.esport.gated.dob.month.september'),
            t('epic.esport.gated.dob.month.october'),
            t('epic.esport.gated.dob.month.november'),
            t('epic.esport.gated.dob.month.december')
        ];

        return Array.from({ length: 12 }, (_, i) => ({
            label: monthNames[i],
            value: (i + 1).toString().padStart(2, '0')
        }));
    };

    const generateDayOptions = (
        month: string,
        year: string
    ): { label: string; value: string }[] => {
        if (!month) {
            return Array.from({ length: 31 }, (_, i) => ({
                label: (i + 1).toString(),
                value: (i + 1).toString().padStart(2, '0')
            }));
        }

        let daysInMonth = 31;

        if (year) {
            daysInMonth = moment(`${year}-${month}`, 'YYYY-MM').daysInMonth();
        } else {
            const monthNum = parseInt(month, 10);
            if (monthNum === 2) {
                daysInMonth = 29;
            } else if ([4, 6, 9, 11].includes(monthNum)) {
                daysInMonth = 30;
            }
        }

        return Array.from({ length: daysInMonth }, (_, i) => ({
            label: (i + 1).toString(),
            value: (i + 1).toString().padStart(2, '0')
        }));
    };

    const validationSchema = yup.object({
        year: yup
            .string()
            .matches(
                /^\d{4}$/,
                t('errors.epic.validation.valied.v2', {
                    field: t('epic.esport.gated.dob.form.label.year')
                })
            )
            .test(
                'valid-year',
                t('errors.epic.validation.valied.v2', {
                    field: t('epic.esport.gated.dob.form.label.year')
                }),
                (value) => {
                    if (!value) return true;
                    const year = parseInt(value, 10);
                    const currentYear = new Date().getFullYear();
                    return year >= 1900 && year <= currentYear;
                }
            )
            .required(t('errors.epic.validation.required')),
        month: yup.string().required(t('errors.epic.validation.required')),
        day: yup.string().required(t('errors.epic.validation.required'))
    });

    const formik = useFormik<DateOfBirthFormValues>({
        initialValues: {
            month: '',
            day: '',
            year: ''
        },
        validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            setDateOfBirth({
                year: values.year,
                month: values.month,
                day: values.day
            });

            const birthDate = `${values.year}-${values.month}-${values.day}`;
            const age = moment().diff(moment(birthDate), 'years');
            if (age < 18) {
                setIsAgeValid(false);
            } else {
                setIsAgeValid(true);
            }
        }
    });

    const handleFieldChange = useCallback(
        (fieldName: string, value: string) => {
            formik.setFieldValue(fieldName, value);
            formik.setFieldTouched(fieldName, true, false);
        },
        [formik]
    );

    const monthOptions = generateMonthOptions();

    const dayOptions = useMemo(
        () => generateDayOptions(formik.values.month, formik.values.year),
        [formik.values.month, formik.values.year]
    );

    return {
        formik,
        monthOptions,
        dayOptions,
        handleFieldChange,
        isAgeValid,
        isConfirmingAge
    };
};
